import React, { useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ReactFlow, {
  Background,
  Controls,
  Position,
  useNodesState,
  useEdgesState,
  Handle,
} from "reactflow";
import "reactflow/dist/style.css";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import { useNotification } from "../../hooks";
import { usePutAiActions } from "../../useQuery";

const getNodeColor = (status) => {
  switch (status) {
    case "FINISHED":
      return "#4caf50";
    case "ONGOING":
      return "#2196f3";
    case "WAITING":
      return "#ff5336";
    case "PENDING":
      return "#ff9800";
    default:
      return "#9e9e9e";
  }
};

const AIStepNode = ({ data, isConnectable }) => {
  const step = data.step;
  const nodeColor = getNodeColor(step.status);

  return (
    <Tooltip title={step.text} placement="top">
      <Box
        sx={{
          minWidth: "200px",
          maxWidth: "300px",
          background: nodeColor,
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          border: "2px solid rgba(255, 255, 255, 0.2)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          style={{
            background: "#fff",
            border: "2px solid #000",
            width: "12px",
            height: "12px",
          }}
        />
        <Box sx={{ p: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontWeight: 600,
              fontSize: "12px",
              lineHeight: 1.3,
              mb: 1,
              textShadow: "0px 1px 2px rgba(0,0,0,0.3)",
            }}
          >
            {step.text}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: "10px",
                textTransform: "uppercase",
                textShadow: "0px 1px 2px rgba(0,0,0,0.3)",
                background: "rgba(0,0,0,0.2)",
                px: 1,
                py: 0.5,
                borderRadius: "4px",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {step.status}
            </Typography>
          </Box>
          {step.status === "WAITING" && (
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                sx={{ background: "#ff9800", minWidth: "76px" }}
                onClick={() => data.onAction?.("skip")}
              >
                Skip
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: "#9C27B0",
                  borderColor: "#9C27B0",
                  minWidth: "76px",
                  "&:hover": { borderColor: "#7B1FA2", color: "#7B1FA2" },
                }}
                onClick={() => data.onAction?.("delete")}
              >
                Delete
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{ background: "#2550d1", minWidth: "96px" }}
                onClick={() => data.onAction?.("continue")}
              >
                Continue
              </Button>
            </Box>
          )}
        </Box>
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          style={{
            background: "#fff",
            border: "2px solid #000",
            width: "12px",
            height: "12px",
          }}
        />
      </Box>
    </Tooltip>
  );
};

const nodeTypes = {
  aiStep: AIStepNode,
};

const AIStepsModal = ({ open, onClose, aiSteps = [], tcrid, AIMessage }) => {
  const theme = useTheme();
  const notify = useNotification();
  const { mutate: putAiAction } = usePutAiActions({
    onSuccess: () => notify("Action submitted successfully", "success"),
    onError: (e) => notify(e?.message || "Failed to submit action", "error"),
  });

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const createNodesAndEdges = useCallback(() => {
    if (!aiSteps.length) return;

    const newNodes = aiSteps.map((step, index) => ({
      id: `step-${index}`,
      type: "aiStep",
      position: {
        x: (index % 3) * 350,
        y: Math.floor(index / 3) * 200,
      },
      data: {
        step,
        onAction: (option) => putAiAction({ option, tcrid }),
      },
    }));

    const newEdges = aiSteps.slice(0, -1).map((_, index) => ({
      id: `edge-${index}`,
      source: `step-${index}`,
      target: `step-${index + 1}`,
      type: "smoothstep",
      animated: true,
      style: {
        stroke: "#2550d1",
        strokeWidth: 2,
      },
    }));

    setNodes(newNodes);
    setEdges(newEdges);
  }, [aiSteps, setNodes, setEdges]);

  React.useEffect(() => {
    if (open && aiSteps.length > 0) {
      createNodesAndEdges();
    }
  }, [open, aiSteps, createNodesAndEdges]);

  React.useEffect(() => {
    if (!open) return;
    const waiting = aiSteps.find((s) => s.status === "WAITING");
    if (waiting) {
      notify(
        "Manual step required in Chrome. Complete it, then return to proceed (Skip/Delete/Continue).",
        "warning",
      );
    }
    if (AIMessage) {
      notify(AIMessage, "info");
    }
  }, [open, aiSteps, notify, AIMessage]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e6f3",
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          AI Steps Flow
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: "relative" }}>
        <Box sx={{ width: "100%", height: "100%" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
            }}
          >
            <Background color="#f5f5f5" />
            <Controls />
          </ReactFlow>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AIStepsModal;
